import React, { useState, useRef, ReactNode, useEffect, useMemo, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import ArrowDown from 'assets/icons/arrow-down.svg'
import useQuery from 'shared/hooks/useQuery'
import DefaultAvatar from 'assets/images/profile-thumbnail.svg'
import useClickOutside from 'shared/hooks/useClickOutside'
import { useDispatch, useSelector } from 'react-redux'
import { authSelector, roleSelector } from 'shared/hocs/withAuth'
import history from 'shared/utils/history'
import { logoutRequest } from 'shared/redux/auth/reducer'
import Triangle from 'assets/icons/dropdown-triangle.svg'
import SvgMyProfileIcon from 'assets/icons/MyProfile'
import SvgLogoutIcon from 'assets/icons/Logout'
import { navListSelector } from './NavigationList'
import { useMenu } from '../MenuContext'
import ProtectedImage from 'shared/components/ProtectedImage'

const SidebarProfile: React.FC = () => {
  const auth = useSelector(authSelector)
  const { isKiosk } = useSelector(roleSelector)
  const { active } = useSelector(navListSelector)
  const dispatch = useDispatch()
  const path = `/${active.split('/')[1]}`
  const theme = useContext(ThemeContext)
  const { auth: user } = auth
  const [isOpened, setIsOpened] = useState(false)
  const optionsRef = useRef(null)
  const menu = useMenu()

  const profileImage = useMemo(() => {
    return (
      <ProtectedImage
        style={{
          borderRadius: '100%',
          width: '50px',
          height: '50px',
          marginRight: '15px',
        }}
        src={user?.photo_url || ''}
        defaultSrc={DefaultAvatar}
      />
    )
  }, [user])

  useClickOutside(optionsRef, () => setIsOpened(false))

  return (
    <SidebarProfileContainer ref={optionsRef}>
      <Profile onClick={() => setIsOpened(!isOpened)}>
        <PhotoAndDetails>
          {profileImage}
          <Details>
            <Name>{[user?.first_name, user?.last_name].filter(Boolean).join(' ')}</Name>
            <Type>{user?.role_display_name || user?.role_name}</Type>
          </Details>
        </PhotoAndDetails>
        <img src={ArrowDown} />
      </Profile>
      {isOpened && (
        <DropdownOptions>
          {!isKiosk && (
            <DropdownItem onClick={handleClick}>
              <Icon>
                {path === '/my-profile' ? <SvgMyProfileIcon fill={theme.colors.main} /> : <SvgMyProfileIcon />}
              </Icon>
              <Label isActive={path === '/my-profile'}>My Profile</Label>
            </DropdownItem>
          )}
          <DropdownItem onClick={() => dispatch(logoutRequest())}>
            <Icon>
              <SvgLogoutIcon />
            </Icon>
            <Label>Log Out of Account</Label>
          </DropdownItem>
        </DropdownOptions>
      )}
    </SidebarProfileContainer>
  )

  function handleClick() {
    history.push('/my-profile')
    menu.hide()
  }
}

export const DropdownOptions = styled.div`
  width: 260px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  position: absolute;
  top: 87px;
  left: 30px;
  border-radius: ${({ theme }) => theme.borderRadius};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 6px rgba(53, 53, 53, 0.2);
  z-index: 16;

  &:before {
    content: '';
    width: 15px;
    height: 10px;
    position: absolute;
    right: 20px;
    top: -10px;
    background: url(${Triangle}) no-repeat;
  }
`

export const DropdownItem = styled.div`
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  &.small {
    height: 35px;
  }

  &.warned {
    color: ${({ theme }) => theme.colors.black};
    background: #ffca64;
    border-radius: 4px;
    margin: 4px;
    border: 1px solid #ffca64;

    &:hover {
      border: 1px solid #664300;
    }
  }

  &.flagged {
    background: #dd5151;
    border-radius: 4px;
    margin: 4px;
    border: 1px solid #dd5151;

    &:hover {
      border: 1px solid #a62121;
    }
  }
`

const SidebarProfileContainer = styled.div`
  position: relative;
  padding: 0 30px 40px;
`

const Profile = styled.div`
  width: 100%;
  height: 70px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius};
  display: flex;
  padding: 0 20px 0 10px;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`

const PhotoAndDetails = styled.div`
  display: flex;
  align-items: center;
`

const Photo = styled.img`
  border-radius: 100%;
  width: 50px;
  height: 50px;
  margin-right: 15px;
`

const Name = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: bold;
  font-size: 16px;
  line-height: 19px;
`

const Type = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.label};
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
`

export const Icon = styled.div`
  margin-right: 15px;
  height: 20px;
`

export const Label = styled.div<{ isActive?: boolean }>`
  font-weight: bold;
  color: ${({ theme, isActive }) => (isActive ? theme.colors.main : theme.colors.secondary)};
`

export default SidebarProfile
